import { Location } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { DialogConfirm, DialogConfirmData } from 'app/dialogs/confirm/dialog-confirm';
import { formatDate } from 'app/helpers/formatDate';
import { InlineBase64UploadAdapter } from 'app/helpers/inlineBase64UploadAdapter';
import { sortCompare } from 'app/helpers/sortCompare';
import { ExposedListJiraIssuesReq } from 'app/interfaces/requests/Exposed/exposedListJiraIssuesReq';
import { JiraCreateIssueDTOReq } from 'app/interfaces/support/jira/jiraCreateIssueDTOReq';
import { JiraCreateIssueFieldsDescriptionDTOs } from 'app/interfaces/support/jira/jiraCreateIssueFieldsDescriptionDTOs';
import { JiraIssuesListRes } from 'app/interfaces/support/jira/jiraIssuesListRes';
import { JiraService } from 'app/services/api/jira/jira.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { HtmlToAdfService } from 'app/services/system/htmltoadf/htmltoadf.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { finalize } from 'rxjs';

@Component({
    selector: 'app-assists-jira-bvh',
    templateUrl: './assists-jira-bvh.component.html',
    styleUrls: ['./assists-jira-bvh.component.scss'],
})
export class AssistsJiraBvhComponent implements OnInit, AfterViewInit {
    @ViewChild('sentinel', { static: false })
    sentinel!: ElementRef;
    private observer!: IntersectionObserver;

    selectedPanel: string = '';
    @Input() selectedPanelIndex: number = 0;

    issues: Array<JiraIssuesListRes> = [];

    pageSize: number = 50;
    isLastPage: boolean = false;
    nextPageToken: string = null;
    orgRequestsColumns: string[] = ['key', 'title', 'created', 'status'];
    displayedData: MatTableDataSource<JiraIssuesListRes> = new MatTableDataSource();
    isLoading: boolean = true;
    loadSessionId: number = 0;
    // Keep track of if the initial fetch of issues is complete
    initialFetchComplete: boolean = false;

    descriptionEditor = ClassicEditor;
    descriptionTextHTML: string;
    navigation: FuseNavigationItem[];

    @ViewChild('ngForm') ngForm: NgForm;

    form: UntypedFormGroup;
    submitted = false;
    formEnabled = true;

    constructor(
        private translationService: TranslationService,
        private dialog: MatDialog,
        private location: Location,
        public jiraService: JiraService,
        public loaderService: LoaderService,
        private alertsService: AlertsService,
        private _formBuilder: UntypedFormBuilder,
        private htmlToAdf: HtmlToAdfService
    ) {
        this.htmlToAdf.preload();
    }

    ngOnInit(): void {
        this.findCorrectTabBasedOnUrl();
        this.initPanelBasedOnUrl();
        this.initForm();
    }

    ngAfterViewInit(): void {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (this.nextPageToken && !this.isLoading) {
                        this.observer.unobserve(entry.target);
                        this.fetchIssues();
                    } else if (this.isLastPage) {
                        this.observer.disconnect();
                    }
                }
            });
        });
    }

    resetObserver() {
        if (!this.observer || !this.sentinel?.nativeElement) return;
        this.observer.disconnect();
        this.observer.observe(this.sentinel.nativeElement);
    }

    private mergeByKey(existing: any[], incoming: any[]) {
        const seen = new Set(existing.map((i) => i.key));
        const merged = [...existing];
        for (const item of incoming) {
            if (!seen.has(item.key)) {
                merged.push(item);
                seen.add(item.key);
            }
        }
        return merged;
    }

    // Map helper to prevent duplicating the mapping code
    private mapIssues(res: any) {
        return (res.issues ?? []).map((issue) => ({
            created: issue.fields.created,
            key: issue.key,
            url: 'https://lsr.atlassian.net/servicedesk/customer/portal/3/' + issue.key,
            title: issue.fields.summary,
            reporterName: issue.fields.reporter.displayName,
            status: issue.fields.status.name,
            eaDate: null,
            ownerName: null,
            implementerName: null,
            assistAmount: null,
            assistAllocationDate: null,
        }));
    }

    initForm() {
        this.submitted = false;
        this.formEnabled = true;

        this.descriptionTextHTML = '';
        this.descriptionEditor = ClassicEditor;
        this.form = this._formBuilder.group({
            summary: new UntypedFormControl('', [Validators.required]),
            description: new UntypedFormControl('', [Validators.required]),
        });
    }

    findCorrectTabBasedOnUrl() {
        try {
            this.selectedPanel = window.location.href.split('/').pop();
        } catch {}
    }

    initPanelBasedOnUrl() {
        if (this.selectedPanel == 'minar-beidnir') {
            this.selectedPanelIndex = 1;
            this.fetchIssues();
        } else {
            this.selectedPanel = 'senda-beidni';
            this.selectedPanelIndex = 0;
        }
    }

    tabChanged(event: MatTabChangeEvent) {
        this.resetList();
        if (event.index == 0) {
            this.location.go('/atvik-og-beidnir/husnaedi/senda-beidni');
        } else if (event.index == 1) {
            this.location.go('/atvik-og-beidnir/husnaedi/minar-beidnir');
            this.fetchIssues();
        }
    }

    formatDate(date) {
        return formatDate(date);
    }

    // Empty the list and reset pagination
    resetList() {
        this.issues = [];
        this.nextPageToken = null;
        this.isLastPage = false;
        this.displayedData.data = this.issues;
        this.initialFetchComplete = false;
    }

    fetchIssues() {
        this.isLoading = true;
        const currentSession = this.loadSessionId;
        const req: ExposedListJiraIssuesReq = {
            NextPageToken: this.nextPageToken,
            PageSize: this.pageSize,
            Project: 'BVH',
            Ssn: null,
        };

        this.jiraService.listIssues(req).subscribe({
            next: (res) => {
                this.isLoading = false;

                if (currentSession !== this.loadSessionId) return;

                const mapped = this.mapIssues(res);
                this.issues = this.mergeByKey(this.issues, mapped);

                this.displayedData.data = this.issues;
                this.isLastPage = res.isLast;
                this.nextPageToken = res.nextPageToken ?? null;
                this.initialFetchComplete = true;

                this.resetObserver();
            },
            error: () => {
                this.isLoading = false;

                this.alertsService.changealertHeader('Villa við að ná í lista');
                this.alertsService.changeAlertErrorMessage('Reyndu aftur síðar.');
                this.alertsService.changeAlertErrorShow(true);
            },
        });
    }

    async submit() {
        this.form.markAllAsTouched();
        this.submitted = true;
        if (this.form.invalid) {
            this.alertsService.changealertHeader('Villur í formi');
            this.alertsService.changeAlertErrorMessage('Vinsamlegast leysið úr villum í formi');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        const dialogRef = this.dialog.open(DialogConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                message: this.translationService.translate('text_confirm_send_request'),
            } as DialogConfirmData,
        });

        dialogRef.afterClosed().subscribe(async (confirm: boolean) => {
            if (!confirm) {
                return;
            }

            this.formEnabled = false;
            this.form.disable();

            try {
                const html = this.form.get('description')!.value ?? '';
                const adfJson = await this.htmlToAdf.convert(html);
                const description: JiraCreateIssueFieldsDescriptionDTOs = JSON.parse(adfJson);

                const createAtvikReq: JiraCreateIssueDTOReq = {
                    fields: {
                        description: description,
                        issueType: {
                            id: '10002',
                        },
                        project: {
                            key: 'BVH',
                        },
                        reporter: null,
                        summary: this.form.get('summary').value,
                        customfield_10010: null,
                        customfield_10066: null,
                        customfield_10067: null,
                        customfield_10068: null,
                        customfield_10111: null,
                        customfield_10069: null,
                        customfield_10075: null,
                        customfield_10098: null,
                        customfield_10099: null,
                        customfield_10078: null,
                        customfield_10079: null,
                        customfield_10081: null,
                        customfield_10085: null,
                    },
                };
                
                this.jiraService.createBVH(createAtvikReq).subscribe((issueKey) => {
                    if (issueKey) {
                        this.initForm();
                        this.selectedPanelIndex = 1;
                        this.resetList();
                        this.loadSessionId++;
                        const currentSession = this.loadSessionId;
                        const req: ExposedListJiraIssuesReq = {
                            NextPageToken: null,
                            PageSize: this.pageSize,
                            Project: 'BVH',
                            Ssn: null,
                            EnsureKey: issueKey,
                        };
                        this.isLoading = true;
                        this.jiraService
                            .listIssues(req)
                            .pipe(finalize(() => (this.isLoading = false)))
                            .subscribe({
                                next: (res) => {
                                    if (currentSession !== this.loadSessionId) return;

                                    this.issues = this.mapIssues(res);

                                    this.displayedData.data = this.issues;
                                    this.isLastPage = res.isLast;
                                    this.nextPageToken = res.nextPageToken ?? null;
                                    this.initialFetchComplete = true;

                                    this.resetObserver();
                                },
                                error: () => {
                                    this.alertsService.changealertHeader('Villa við að ná í lista');
                                    this.alertsService.changeAlertErrorMessage('Reyndu aftur síðar.');
                                    this.alertsService.changeAlertErrorShow(true);
                                },
                            });
                    } else {
                        this.formEnabled = true;
                        this.submitted = false;
                        this.form.enable();
                        this.initForm();
                    }
                });
            } catch (e) {
                this.alertsService.changealertHeader('Villa við breytingu lýsingar');
                this.alertsService.changeAlertErrorMessage('Tókst ekki að breyta yfir í ADF.');
                this.alertsService.changeAlertErrorShow(true);
                this.submitted = false;
                this.formEnabled = true;
                this.form.enable();
            }
        });
    }

    public descriptionOnChange({ editor }: ChangeEvent) {
        this.form.markAsDirty();
        this.descriptionTextHTML = editor.getData();
        this.form.get('description').setValue(editor.getData());
    }

    public onEditorReady(editor: any) {
        editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => new InlineBase64UploadAdapter(loader);
    }

    public editorConfig: any = {
        alignment: { options: ['left', 'right', 'center', 'justify'] },
        toolbar: ['heading', '|', 'bold', 'italic', 'bulletedList', 'numberedList', 'link', 'imageUpload', '|', 'undo', 'redo'],
        image: { toolbar: ['toggleImageCaption', 'imageTextAlternative'] },
    };

    //#region Table Sort and Filter

    searchFilter(filterValue: string) {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.displayedData.filter = filterValue;
    }

    sortData(sort: Sort) {
        const data = this.displayedData.data.slice();
        if (!sort.active || sort.direction === '') {
            this.displayedData.data = data;
            return;
        }

        // orgRequestsColumns: string[] = ['key', 'title', 'created', 'status'];

        this.displayedData.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'key':
                    return sortCompare(a.key, b.key, isAsc);
                case 'title':
                    return sortCompare(a.title, b.title, isAsc);
                case 'created':
                    return sortCompare(a.created, b.created, isAsc);
                case 'status':
                    return sortCompare(a.status, b.status, isAsc);
                default:
                    return 0;
            }
        });
    }

    //#endregion

    revertAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við?')) {
            this.form.markAsPristine();
            this.initForm();
        }
    }
}
